/**
 * Locale catalog shared by the Worker, the client and the email templates.
 */

export const SUPPORTED_LOCALES = ['zh-CN', 'en'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'zh-CN'

export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/**
 * Resolves the locale of a request. Priority: explicit request value, stored
 * user preference, `Accept-Language` header, then the default.
 */
export function resolveLocale(input: {
  requested?: string | null
  userLocale?: string | null
  acceptLanguage?: string | null
  fallback?: Locale
}): Locale {
  const candidates = [input.requested, input.userLocale]
  for (const candidate of candidates) {
    if (candidate && isLocale(candidate)) {
      return candidate
    }
  }

  const header = input.acceptLanguage ?? ''
  for (const part of header.split(',')) {
    const tag = part.split(';')[0]?.trim()
    if (!tag) {
      continue
    }
    if (isLocale(tag)) {
      return tag
    }
    const base = tag.split('-')[0]?.toLowerCase()
    if (base === 'zh') {
      return 'zh-CN'
    }
    if (base === 'en') {
      return 'en'
    }
  }

  return input.fallback ?? DEFAULT_LOCALE
}
